import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const { phone, address, checkInTime, checkOutTime, getWhatsAppLink } = useBusinessInfo();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [faqs, setFaqs] = useState([]);

  // Fetch FAQs from backend
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data || []);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    };
    fetchFaqs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error(t('contact.requiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        message: formData.message
      });

      setSubmitted(true);
      toast.success(language === 'es' ? '¡Mensaje enviado! Te contactaremos pronto.' : 'Message sent! We will contact you soon.');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('contact.errorSending'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#F5F2EB] pt-24">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <section className="py-16 bg-white">
        <div className="container-ribba text-center">
          <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
            Cabañas RIBBA
          </span>
          <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] font-bold mt-3 mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-[#5A5A5A] max-w-2xl mx-auto">
            {t('contact.pageDescription')}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container-ribba">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-8">
                {t('contact.contactInfo')}
              </h2>

              <div className="space-y-6">
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="flex items-start gap-4 p-6 bg-white rounded-sm card-shadow hover:shadow-lg transition-shadow group"
                >
                  <div className="w-12 h-12 bg-[#25D366]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366] transition-colors">
                    <Phone className="w-6 h-6 text-[#25D366] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.whatsapp')}</h3>
                    <p className="text-[#2F3E30] font-medium">{phone}</p>
                    <p className="text-[#5A5A5A] text-sm mt-1">
                      {t('contact.whatsappDesc')}
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-white rounded-sm card-shadow">
                  <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#2F3E30]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.location')}</h3>
                    <p className="text-[#5A5A5A]">{address}</p>
                    <p className="text-[#5A5A5A] text-sm mt-1">
                      {t('contact.locationDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white rounded-sm card-shadow">
                  <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#2F3E30]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.schedule')}</h3>
                    <p className="text-[#5A5A5A]">{t('contact.scheduleDesc')}</p>
                    <p className="text-[#5A5A5A] text-sm mt-1">
                      Check-in: {checkInTime} | Check-out: {checkOutTime}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-10 p-6 bg-[#2F3E30] rounded-sm text-white">
                <h3 className="font-heading text-xl font-semibold mb-4">
                  {t('contact.readyToBook')}
                </h3>
                <p className="text-white/70 mb-4">
                  {t('contact.readyToBookDesc')}
                </p>
                <Link
                  to="/reservar"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-[#1A1A1A] font-medium rounded-sm hover:bg-white transition-colors"
                >
                  {t('contact.goToBook')}
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-sm card-shadow">
              {submitted ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <div className="w-16 h-16 bg-[#3D6F48]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#3D6F48]" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-3">
                    {t('contact.success')}
                  </h3>
                  <p className="text-[#5A5A5A] mb-6">
                    {t('contact.successDesc')}
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', phone: '', email: '', message: '' });
                    }}
                    className="text-[#2F3E30] font-medium hover:underline"
                  >
                    {t('contact.sendAnother')}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">
                    {t('contact.sendMessage')}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <Label htmlFor="contact-name" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {t('contact.name')} *
                      </Label>
                      <Input
                        id="contact-name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t('booking.yourName')}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-phone" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {t('contact.phoneWhatsApp')} *
                      </Label>
                      <Input
                        id="contact-phone"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+52 33 1234 5678"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-email" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {t('contact.emailOptional')}
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="tu@email.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-message" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {t('contact.message')} *
                      </Label>
                      <Textarea
                        id="contact-message"
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder={t('contact.writeMessage')}
                        rows={5}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={submitting}
                      className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          {t('contact.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {t('contact.send')}
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container-ribba">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-[#1A1A1A]">
              {t('contact.faq')}
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.length > 0 ? (
              faqs.map((faq) => (
                <div key={faq.id} className="p-6 bg-[#F5F2EB] rounded-sm">
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">
                    {language === 'es' ? faq.question_es : (faq.question_en || faq.question_es)}
                  </h3>
                  <p className="text-[#5A5A5A] text-sm">
                    {language === 'es' ? faq.answer_es : (faq.answer_en || faq.answer_es)}
                  </p>
                </div>
              ))
            ) : (
              // Fallback to hardcoded FAQs if API fails
              [
                { q: t('contact.faqCheckIn'), a: `${language === 'es' ? 'El check-in es a partir de las' : 'Check-in is from'} ${checkInTime} ${language === 'es' ? 'y el check-out hasta las' : 'and check-out is until'} ${checkOutTime}. ${language === 'es' ? 'Podemos ajustar horarios según disponibilidad.' : 'We can adjust times based on availability.'}` },
                { q: t('contact.faqPets'), a: t('contact.faqPetsAnswer') },
                { q: t('contact.faqPayment'), a: t('contact.faqPaymentAnswer') },
                { q: t('contact.faqIncludes'), a: t('contact.faqIncludesAnswer') }
              ].map((faq, idx) => (
                <div key={idx} className="p-6 bg-[#F5F2EB] rounded-sm">
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">{faq.q}</h3>
                  <p className="text-[#5A5A5A] text-sm">{faq.a}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
